import React from 'react';
import { useIntl } from 'react-intl';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { profileImageSpec, type ArtistProfileKind } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { HeaderPhotoPicker } from '../../studio/spotifyHeader/HeaderPhotoPicker';
import { HeaderPicturePicker } from '../../studio/spotifyHeader/HeaderPicturePicker';
import type { ArtistProfile } from '../../studio/spotifyHeader/useArtistProfile';
import type { HeaderPictures } from '../../studio/spotifyHeader/useHeaderPictures';
import { spotifyHeaderMessages } from '../../studio/spotifyHeader/spotifyHeaderMessages';
import type { ProfileRelease } from '../profileReleases';
import type { ProfileToolDraft } from './profileToolDraft';
import { ProfileToolRelease } from './ProfileToolRelease';
import { profileToolMessages as t } from './profileToolMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** Spotify asks for an artist photo of 750 px or more (`SPOTIFY_PROFILE_FILE_RULES.avatar`). */
const AVATAR_MIN_PX = 750;

interface ProfileSourcePickersProps {
  accountId: number;
  kind: ArtistProfileKind;
  draft: ProfileToolDraft;
  onChange: (patch: Partial<ProfileToolDraft>) => void;
  releases: readonly ProfileRelease[];
  pictures: HeaderPictures;
  artist: ArtistProfile;
  artistThemeId: ReleaseThemeId;
  /** The title of the release whose AI pictures show, or null with no release. */
  aiReleaseTitle: string | null;
}

/**
 * The picker of the picked source (design b1–b4): the release of a cover, the photo (the profile
 * photo or an upload, with the small-photo warning of the kind), the bank or the AI pictures.
 */
const ProfileSourcePickersComponent: React.FC<ProfileSourcePickersProps> = (props) => {
  const { accountId, kind, draft, onChange, releases, pictures, artist, artistThemeId, aiReleaseTitle } = props;
  const intl = useIntl();
  const spec = profileImageSpec(kind);
  const minWidth = kind === 'spotifyAvatar' ? AVATAR_MIN_PX : spec.width;

  switch (draft.source) {
    case 'cover':
      return (
        <ProfileToolRelease
          releases={releases}
          value={draft.catalogItemUuid}
          hasLookSwitch={spec.coverMode === 'blur'}
          lookFromRelease={draft.lookFromRelease}
          artistThemeId={artistThemeId}
          onPick={(catalogItemUuid) => onChange({ catalogItemUuid })}
          onLookChange={(lookFromRelease) => onChange({ lookFromRelease })}
        />
      );
    case 'photo':
      return (
        <HeaderPhotoPicker
          accountId={accountId}
          profile={artist}
          fileId={draft.fileId}
          photoUrl={draft.photoUrl}
          minWidth={minWidth}
          hint={
            kind === 'spotifyAvatar'
              ? intl.formatMessage(t.avatarPhotoHint)
              : kind === 'pressPhoto'
                ? intl.formatMessage(t.pressPhotoHint)
                : intl.formatMessage(t.photoHintWide, { width: spec.width })
          }
          smallPhotoText={(width) =>
            kind === 'spotifyAvatar'
              ? intl.formatMessage(t.avatarSmallPhoto, { width })
              : intl.formatMessage(t.smallPhoto, { width, target: spec.width })
          }
          onPick={(photo) => onChange({ fileId: photo.fileId, photoUrl: photo.url })}
        />
      );
    case 'bank':
      return (
        <HeaderPicturePicker
          testId="profile-bank-picker"
          title={intl.formatMessage(spotifyHeaderMessages.bankTitle)}
          hint={intl.formatMessage(spotifyHeaderMessages.bankHint, {
            theme: intl.formatMessage(releaseThemeMessages[artistThemeId].name),
          })}
          pictures={pictures.bank}
          value={draft.pictureUuid}
          empty={<span className={styles.meta}>{intl.formatMessage(spotifyHeaderMessages.bankEmpty)}</span>}
          onPick={(picture) => onChange({ pictureUuid: picture.uuid })}
        />
      );
    case 'ai':
      return (
        <HeaderPicturePicker
          testId="profile-ai-picker"
          title={
            aiReleaseTitle === null
              ? intl.formatMessage(t.sourceAi)
              : intl.formatMessage(t.aiTitle, { title: aiReleaseTitle })
          }
          hint={intl.formatMessage(spotifyHeaderMessages.aiHint)}
          pictures={pictures.ai}
          value={draft.pictureUuid}
          empty={
            <span className={styles.meta}>
              {aiReleaseTitle === null
                ? intl.formatMessage(t.aiNoRelease)
                : intl.formatMessage(spotifyHeaderMessages.aiNone, { title: aiReleaseTitle })}
            </span>
          }
          onPick={(picture) => onChange({ pictureUuid: picture.uuid })}
        />
      );
    case 'theme':
      return null;
  }
};

export const ProfileSourcePickers = TypedMemo(ProfileSourcePickersComponent);
